<template>
    <div class="payment-wrapper">
        <div class="payment-header">
            <p class="title">{{ paymentTitle }}</p>
            <div class="payment-card-wrapper">
                <div v-if="favPayment" class="fav-card">
                    <div class="card-info">
                        <p class="card-info-top">
                            {{ `${favPayment.brand} ending in ${favPayment.last_four}` }}
                        </p>
                        <div class="card-info-bottom">
                            <p>Expiry</p>
                            <p>
                                {{
                                    `${favPayment.exp_month}/${favPayment.exp_year.slice(-2)}`
                                }}
                            </p>
                        </div>
                    </div>
                    <img :src="URLS.cards[favPayment.brand]" alt="" class="card-logo" />
                </div>
                <div
                    v-if="otherPayments.length"
                    @click="() => (dropdownOpen = !dropdownOpen)"
                    class="other-cards-dropdown"
                >
                    <p class="dropdown-title">Other cards</p>
                    <img
                        :src="URLS.icons.dropdownIcon"
                        alt="dropdown-icon"
                        class="dropdown-icon"
                    />
                </div>
                <div class="dropdown">
                    <OtherCardsDropdown
                        :showOtherCards="dropdownOpen"
                        :otherCards="otherPayments"
                    />
                </div>
                <button
                    v-if="!showAddCardForm"
                    @click="() => (showAddCardForm = true)"
                    class="add-card-btn"
                >
                    Add New Card
                </button>
                <AddCardForm v-else />
            </div>
        </div>
        <PaymentSummary :role="role" />
        <slot name="payment-actions"></slot>
    </div>
</template>
<script>
    import OtherCardsDropdown from '@/components/payment-methods/OtherCardsDropdown'
    import AddCardForm from '@/components/payment-methods/AddCardForm'
    import PaymentSummary from '@/components/subscriptions/PaymentSummary'
    import { usePaymentMethods } from '@/composables/features/payments-methods/usePayments'
    import { useSubscription } from '@/composables/features/subscriptions/useSubscriptions'

    const URLS = {
        icons: {
            dropdownIcon: '/images/icons/down-arrow-gray.svg'
        },
        cards: {
            'American Express': '/images/icons/payment-details/cc-amex.svg',
            Visa: '/images/icons/payment-details/cc-visa.svg',
            MasterCard: '/images/icons/payment-details/cc-mastercard.svg',
            Discover: '/images/icons/payment-details/cc-discover.svg',
            UnionPay: '/images/icons/payment-details/UnionPay.svg',
            DinaCard: '/images/icons/payment-details/DinaCard.svg',
            'Diners Club': '/images/icons/payment-details/DinersClub.svg',
            BCCard: '/images/icons/payment-details/BCCard.svg',
            JCB: '/images/icons/payment-details/JSB.svg',
            others: '/images/icons/payment-details/cc-regular.svg'
        }
    }

    export default {
        name: 'PaymentMethods',
        components: {
            AddCardForm,
            OtherCardsDropdown,
            PaymentSummary
        },
        props: {
            role: {
                type: String,
                required: true
            }
        },
        setup(props, context) {
            const {
                showAddCardForm,
                dropdownOpen,
                duplicateCard,
                userPaymentMethods,
                favPayment,
                otherPayments,
                addPaymentMethod,
                formatCreditCardInput
            } = usePaymentMethods()
            const { selectedPlan, monthPrice, canTrial } = useSubscription(props, context)

            return {
                dropdownOpen,
                duplicateCard,
                showAddCardForm,
                userPaymentMethods,
                favPayment,
                otherPayments,
                formatCreditCardInput,
                addPaymentMethod,
                selectedPlan,
                monthPrice,
                canTrial
            }
        },
        computed: {
            URLS: () => URLS,
            paymentTitle: (self) => (self.canTrial ? 'Confirm Your Trial' : 'Payment')
        }
    }
</script>
<style lang="scss" scoped>
    @import '../../../sass/_variables.scss';
    $mobileMax: 600px;

    @media screen and (max-width: $mobileMax) {
        .payment-wrapper {
            .payment-header {
                .title {
                    font-size: 26px !important;
                }

                .payment-card-wrapper {
                    .fav-card {
                        .card-info {
                            width: 100%;
                        }
                    }
                }
            }
        }
    }
    p {
        margin-bottom: 0px;
    }
    button {
        border: none;
    }
    .payment-wrapper {
        display: flex;
        flex-direction: column;
        width: 100%;
        justify-content: center;
        align-items: center;
        gap: 40px;

        .payment-header {
            width: 100%;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            gap: 24px;

            .title {
                align-self: flex-start;
                color: #dedede;
                font-family: 'Instrument Sans';
                font-size: 32px;
                font-style: normal;
                font-weight: 500;
                line-height: 120%;
                letter-spacing: -0.64px;
            }

            .payment-card-wrapper {
                width: 100%;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                gap: 16px;

                .fav-card {
                    width: 100%;
                    display: flex;
                    flex-direction: row;
                    justify-content: center;
                    align-items: center;
                    gap: 16px;
                    padding: 12px 16px;
                    border-radius: 16px;
                    border: 1px solid #cdeb09;

                    .card-info {
                        width: 278px;
                        display: flex;
                        flex-direction: column;
                        align-items: flex-start;
                        justify-content: center;
                        gap: 4px;

                        p {
                            color: #dedede;
                            font-family: 'Instrument Sans';
                            font-size: 14px;
                            font-style: normal;
                            font-weight: 500;
                            line-height: 115%;
                            letter-spacing: 0.14px;
                        }

                        .card-info-top {
                            text-align: left;
                        }
                        .card-info-bottom {
                            display: flex;
                            flex-direction: row;
                            align-items: center;
                            justify-content: flex-start;
                            gap: 4px;
                        }
                    }
                    .card-logo {
                        width: 54px;
                        height: 36px;
                        object-fit: cover;
                    }
                }
                .other-cards-dropdown {
                    align-self: stretch;
                    border-radius: 8px;
                    border: 1px solid #8b8b8b;
                    display: flex;
                    padding: 10px 12px;
                    flex-direction: row;
                    justify-content: space-between;
                    align-items: center;
                    cursor: pointer;

                    .dropdown-title {
                        color: #646464;
                        font-family: 'Instrument Sans';
                        font-size: 16px;
                        font-style: normal;
                        font-weight: 500;
                        line-height: 125%;
                    }
                    .dropdown-icon {
                        width: 20px;
                        height: 20px;
                        object-fit: cover;
                    }
                }
                .dropdown {
                    position: relative;
                    max-width: 385px;
                    width: 100%;
                }
                .add-card-btn {
                    display: flex;
                    padding: 10px 24px;
                    justify-content: center;
                    align-items: center;
                    gap: 8px;
                    align-self: stretch;
                    border-radius: 120px;
                    border: 1px solid #8b8b8b;
                    background: rgba(0, 0, 0, 0);
                    color: #8b8b8b;
                    font-family: 'Instrument Sans';
                    font-size: 16px;
                    font-style: normal;
                    font-weight: 500;
                    line-height: 125%;
                }
            }
        }
    }
</style>
